var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { forwardRef } from 'react';
import clsx from 'clsx';
import { twMerge } from 'tailwind-merge';
import { useBackgroundContext } from '../BackgroundContext/index.js';
import { Icon } from '../Icon/index.js';
import { SpinnerIcon } from '../icons/index.js';
import { Tooltip } from '../Tooltip/index.js';
export const BUTTON_VARIANTS = [
    'primary',
    'secondary',
    'outline',
    'danger',
    'basic',
];
export const BUTTON_SIZES = ['xs', 'sm', 'md', 'lg', 'xl'];
export const Button = forwardRef((_a, ref) => {
    var _b;
    var { as, variant = 'primary', size = 'md', fullWidth = false, rounded = false, disabled = false, loading = false, className, children, leadingIcon, trailingIcon, tooltip } = _a, rest = __rest(_a, ["as", "variant", "size", "fullWidth", "rounded", "disabled", "loading", "className", "children", "leadingIcon", "trailingIcon", "tooltip"]);
    const { backgroundType } = useBackgroundContext();
    const sizeClsx = [
        'font-medium shadow-sm',
        size === 'xs' && 'px-2.5 py-1.5 text-xs',
        size === 'sm' && 'px-3 py-2 text-sm leading-4',
        size === 'md' && 'px-4 py-2 text-sm',
        size === 'lg' && 'px-4 py-2 text-base',
        size === 'xl' && 'px-6 py-3 text-base',
    ];
    const roundedSizeClsx = [
        'font-medium shadow-sm',
        size === 'xs' && 'px-1.5 py-1.5 text-xs',
        size === 'sm' && 'px-2 py-2 text-sm leading-4',
        size === 'md' && 'px-2 py-2 text-sm',
        size === 'lg' && 'px-2 py-2 text-base',
        size === 'xl' && 'px-3 py-3 text-base',
    ];
    const textColorClsx = [
        variant === 'primary' &&
            'text-content-on-primary bg-action-primary hover:bg-action-primary-hovered disabled:bg-action-primary-disabled disabled:text-content-disabled',
        variant === 'secondary' &&
            'text-content-primary bg-action-primary-subdued hover:bg-action-primary-subdued-hovered disabled:bg-action-primary-disabled disabled:text-content-disabled',
        variant === 'outline' && [
            backgroundType === 'main' &&
                'text-content-subdued bg-surface hover:bg-surface-hovered disabled:bg-surface-disabled disabled:text-content-disabled',
            backgroundType === 'surface' &&
                'text-content-subdued bg-surface hover:bg-surface-hovered disabled:bg-surface-disabled disabled:text-content-disabled',
            backgroundType === 'secondary' &&
                'text-content-on-topbar bg-topbar hover:bg-topbar-subdued disabled:bg-topbar disabled:text-content-disabled',
        ],
        variant === 'basic' && [
            backgroundType === 'main' &&
                'text-content-on-background-subdued bg-background hover:bg-background-hovered disabled:bg-background-disabled disabled:text-content-on-background-disabled',
            backgroundType === 'surface' &&
                'text-content-subdued bg-surface hover:bg-surface-hovered disabled:bg-surface-disabled disabled:text-content-disabled',
            backgroundType === 'secondary' &&
                'text-content-on-topbar bg-topbar hover:bg-topbar disabled:bg-topbar disabled:text-content-disabled',
        ],
        variant === 'danger' &&
            'text-content-on-destructive bg-action-destructive ring-action-destructive hover:bg-action-destructive-hovered disabled:bg-action-destructive-disabled disabled:text-content-disabled',
    ];
    const borderClsx = [
        'border',
        variant === 'primary' && 'border-transparent',
        variant === 'secondary' && 'border-transparent',
        variant === 'outline' && 'border-line',
        variant === 'basic' && ['shadow-none border-none'],
        variant === 'danger' && 'border-transparent focus:ring-on-error',
    ];
    const roundedClsx = [
        !rounded && size === 'xs' && 'rounded',
        !rounded && size !== 'xs' && 'rounded-md',
        rounded && 'rounded-full ',
    ];
    const contentProps = { trailingIcon, leadingIcon, size, children };
    const Component = tooltip ? Tooltip.Trigger : as || 'button';
    if (((_b = as) === null || _b === void 0 ? void 0 : _b.name) === 'Link') {
        rest = Object.assign(Object.assign({}, rest), { variant: 'inherit' });
    }
    const button = (React.createElement(Component, Object.assign({ ref: ref, type: as ? undefined : 'button', className: twMerge(clsx('inline-flex items-center relative', 'focus:outline-none focus-visible:ring', textColorClsx, !rounded ? sizeClsx : roundedSizeClsx, roundedClsx, borderClsx, fullWidth && 'w-full flex justify-center max-w-full', disabled &&
            'cursor-default pointer-events-none disabled:border-line-disabled', loading && 'opacity-60 cursor-default pointer-events-none', className)), disabled: disabled }, rest),
        React.createElement(React.Fragment, null,
            loading && (React.createElement("div", { className: "absolute inline-flex start-0 end-0 justify-center" },
                React.createElement(SpinnerIcon, { className: "animate-spin" }))),
            React.createElement("span", { className: clsx('flex min-w-0', { invisible: loading }) },
                React.createElement(ButtonContent, Object.assign({}, contentProps))))));
    if (tooltip) {
        return (React.createElement(Tooltip, null,
            button,
            React.createElement(Tooltip.Panel, null, tooltip)));
    }
    return button;
});
const ButtonContent = ({ leadingIcon, trailingIcon, children, size, }) => {
    const iconSizeClsx = [
        size === 'xs' && 'h-4 w-4',
        size === 'sm' && 'h-4 w-4',
        size === 'md' && 'h-5 w-5',
        size === 'lg' && 'h-6 w-6',
        size === 'xl' && 'h-6 w-6',
    ];
    const leadingIconPaddingClsx = [
        ['xs', 'sm'].includes(size) && '-ms-0.5 me-2',
        ['md'].includes(size) && '-ms-1 me-2',
        ['lg', 'xl'].includes(size) && '-ms-1 me-3',
    ];
    const trailingIconPaddingClsx = [
        ['xs', 'sm'].includes(size) && 'ms-2 -me-0.5',
        ['md'].includes(size) && 'ms-2 -me-1 ',
        ['lg', 'xl'].includes(size) && 'ms-3 -me-1 ',
    ];
    return (React.createElement("span", { className: "inline-flex items-center min-w-0" },
        leadingIcon && (React.createElement(Icon, { className: clsx(iconSizeClsx, !!children && leadingIconPaddingClsx) }, leadingIcon)),
        children,
        trailingIcon && (React.createElement(Icon, { className: clsx(iconSizeClsx, !!children && trailingIconPaddingClsx) }, trailingIcon))));
};
//# sourceMappingURL=Button.js.map